import api from './api'
import {
  Notificacao,
  NotificacaoResponse,
  NotificacaoCountResponse,
  NotificacaoResumo,
  PreferenciasNotificacao,
} from '@/types/notificacao'
import { ApiResponse } from '@/types/comum'

const BASE_URL = '/api/v1/notificacoes'

/**
 * Notificações do usuário logado.
 *
 * O destinatário NÃO é mais enviado pelo cliente: o backend o deriva do token
 * ({sub, email}). Antes, cada chamada mandava `?destinatario=<email>` e o servidor
 * confiava nesse valor. Além do furo de acesso, o e-mail nunca casava com as
 * notificações IN_APP — os consumidores Kafka as endereçam pelo UUID do usuário —,
 * então nenhuma delas aparecia na UI.
 */
export const notificacaoService = {
  listar: async (page: number = 0, size: number = 10): Promise<NotificacaoResponse> => {
    const response = await api.get<NotificacaoResponse>(`${BASE_URL}?page=${page}&size=${size}`)
    return response as unknown as NotificacaoResponse
  },

  recentes: async (limite: number = 5): Promise<NotificacaoResumo[]> => {
    const page = (await api.get(`${BASE_URL}?page=0&size=${limite}`)) as unknown as {
      content?: NotificacaoResumo[]
    }
    return page?.content ?? []
  },

  buscar: async (id: string): Promise<Notificacao> => {
    const response = await api.get<ApiResponse<Notificacao>>(`${BASE_URL}/${id}`)
    return (response as unknown as ApiResponse<Notificacao>).data
  },

  marcarLida: async (id: string): Promise<void> => {
    await api.put(`${BASE_URL}/${id}/lida`)
  },

  /**
   * Preferências de e-mail por categoria (Fase 6, item 23) — do usuário do token. Governam
   * só o espelhamento por e-mail; o in-app não se desliga. Sem registro = tudo ligado.
   */
  obterPreferencias: async (): Promise<PreferenciasNotificacao> => {
    const r = await api.get<PreferenciasNotificacao>(`${BASE_URL}/preferencias`)
    return r as unknown as PreferenciasNotificacao
  },

  salvarPreferencias: async (prefs: PreferenciasNotificacao): Promise<PreferenciasNotificacao> => {
    const r = await api.put<PreferenciasNotificacao>(`${BASE_URL}/preferencias`, prefs)
    return r as unknown as PreferenciasNotificacao
  },

  marcarTodasLidas: async (): Promise<void> => {
    await api.put(`${BASE_URL}/todas-lidas`)
  },

  obterContagem: async (): Promise<NotificacaoCountResponse> => {
    // Backend devolve um Long puro em /nao-lidas/count
    const count = (await api.get<number>(`${BASE_URL}/nao-lidas/count`)) as unknown as number
    return { naoLidas: count ?? 0, total: count ?? 0 } as NotificacaoCountResponse
  },
}
